import { Container, Graphics, Text, type FederatedPointerEvent } from "pixi.js";
import { HORIZON, type DraggableId, type Placement } from "../game/types";
import { LILY_LEAF, stateAt } from "../game/world";
import type { EditStore } from "../game/editStore";
import { P, clamp01, lerp, mix, ramp } from "./palette";
import * as art from "./art";

const W = 1024;
const H = 768;

// Пологая дуга часов по верху: цифры 0..10 и кончик стрелки.
// Ступица под табличкой с числом; стрелка указывает на текущую цифру.
const HUB = { x: 512, y: 150 };
const numX = (t: number) => 150 + (t / HORIZON) * 724; // 150..874
const numY = (t: number) => 170 - Math.sin((t / HORIZON) * Math.PI) * 62; // купол: 170 по краям, ~108 в центре

// Тропинка из 11 плоских камней по низу луга.
const pebbleX = (i: number) => 116 + i * 79;
const pebbleY = (i: number) => 700 + Math.sin(i * 0.7) * 7;

// Базовый масштаб жителей (референс: крупный передний план).
const BASE: Record<string, number> = { frog: 1.34, snail: 1.16, hen: 1.14, chick: 1.04, salad: 1.12, fly: 1.12, egg: 1 };

// Точки жителей в мире-сцене.
const POND = { x: 244, y: 604 };
const FROG_POND = { x: 214, y: 624 };
const FROG_LEAF = { x: LILY_LEAF.x, y: LILY_LEAF.y - 18 };
const NEST = { x: 858, y: 588 };
const MOSS = { x: 772, y: 610 };
const EGG_NEST = { x: 858, y: 576 };
const HEN_NEST = { x: 832, y: 552 };
const HEN_FENCE = { x: 628, y: 520 };
const CUCKOO_HOME = { x: 232, y: 176 };

export interface SceneCallbacks {
  onEdit(objectId: DraggableId, tick: number, placement: Placement): void;
  onDragStart(objectId: DraggableId): void;
}

export class Scene {
  readonly container = new Container();
  private edits: EditStore;
  private cb: SceneCallbacks;

  private frozenMoment: number | null = null;
  private rewinding = false;
  private draggingId: DraggableId | null = null;
  private dragOffset = { x: 0, y: 0 };
  private cuckooOut = false;

  // Слои (сзади → вперёд)
  private skyBase = new Graphics();
  private horizonGlow = new Graphics();
  private stars = new Container();
  private sun = new Graphics();
  private moon = new Graphics();
  private clouds = new Container();
  private bg = new Container(); // холмы, мельница, домики, мост, дальние деревья
  private windmillBlades: Container | null = null;
  private cottageWins: Graphics[] = [];
  private ground = new Container();
  private pondC = new Container();
  private ripples = new Graphics();
  private decor = new Container(); // цветы, трава, камни, дерево, забор
  private grassTufts: Container[] = [];
  private reeds = art.makeReeds();
  private shadows = new Container();
  private shFrog = new Graphics();
  private shSnail = new Graphics();
  private shHen = new Graphics();
  private shChick = new Graphics();
  private shSalad = new Graphics();
  private warmLight = new Graphics();

  private clockRing = new Container();
  private hand = new Graphics();
  private bigDigit = new Text({
    text: "0",
    style: { fontSize: 82, fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: "700", fill: 0xfff4da },
  });
  private bigDigitShadow = new Text({
    text: "0",
    style: { fontSize: 82, fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: "700", fill: P.woodGrain },
  });
  private digitShown = -1;
  private digitPop = 0;

  // Жители / предметы
  private birdhouse = art.makeBirdhouse();
  private cuckoo = art.makeCuckoo();
  private bubble = new Container();
  private bubbleText = new Text({
    text: "",
    style: {
      fontSize: 22,
      fontFamily: "Georgia, serif",
      fill: P.bubbleInk,
      align: "center",
      wordWrap: true,
      wordWrapWidth: 210,
    },
  });
  private bubbleUntil = 0;

  private snail = art.makeSnail();
  private frog = art.makeFrog();
  private fly = art.makeFly();
  private hen = art.makeHen();
  private egg = art.makeEgg();
  private chick = art.makeChick();
  private salad = art.makeSalad();
  private warmthDots = new Container();
  private thought = new Container();
  private nightVeil = new Graphics();
  private warmVeil = new Graphics();
  private rewindVeil = new Graphics();
  private sparkles = new Container();

  constructor(edits: EditStore, cb: SceneCallbacks) {
    this.edits = edits;
    this.cb = cb;
    this.build();
    this.bindDrag();
  }

  // ---------------------------------------------------------------- build
  private build() {
    const c = this.container;

    c.addChild(this.skyBase, this.horizonGlow, this.stars, this.sun, this.moon, this.clouds);
    this.buildBackground();
    c.addChild(this.bg);
    this.buildGround();
    c.addChild(this.ground);
    this.buildPond();
    c.addChild(this.pondC);
    this.buildDecor();
    c.addChild(this.decor);

    // Контактные тени жителей (мягкие эллипсы на земле)
    for (const s of [this.shFrog, this.shSnail, this.shHen, this.shChick, this.shSalad]) {
      s.ellipse(0, 0, 30, 10).fill({ color: P.night, alpha: 0.16 });
      this.shadows.addChild(s);
    }
    c.addChild(this.shadows);

    // Жители
    this.warmthDots.position.set(0, 0);
    this.buildThought();
    c.addChild(
      this.salad.node,
      this.snail.node,
      this.egg.node,
      this.hen.node,
      this.chick.node,
      this.frog.node,
      this.fly.node,
      this.warmthDots,
      this.thought,
      this.sparkles,
    );

    // Тёплое свечение из верхнего-левого угла (мягкий золотой час, без перегруза)
    for (let r = 560; r > 40; r -= 46) {
      this.warmLight.circle(210, 70, r).fill({ color: P.sunGold, alpha: 0.02 });
    }
    // лёгкая виньетка по нижним углам — «объём» сцены
    this.warmLight.rect(0, 620, W, 148).fill({ color: P.woodGrain, alpha: 0.06 });
    this.warmLight.eventMode = "none";
    c.addChild(this.warmLight);

    // Часы поверх мира
    this.buildClock();
    c.addChild(this.clockRing);
    // Листва в углах — поверх арки
    c.addChild(this.buildFrameLeaves());

    // Домик Кукушки висит на часах
    this.birdhouse.position.set(CUCKOO_HOME.x, CUCKOO_HOME.y);
    this.cuckoo.node.position.set(CUCKOO_HOME.x, CUCKOO_HOME.y + 6);
    this.cuckoo.node.scale.set(1.02);
    c.addChild(this.birdhouse, this.cuckoo.node);
    this.buildBubble();
    c.addChild(this.bubble);

    // Наложения времени суток и перемотки — поверх всего мира, под часами? нет, поверх.
    for (const v of [this.warmVeil, this.nightVeil, this.rewindVeil]) {
      v.rect(0, 0, W, H).fill(0xffffff);
      v.alpha = 0;
    }
    this.nightVeil.clear().rect(0, 0, W, H).fill(P.night);
    this.warmVeil.clear().rect(0, 0, W, H).fill(P.warm);
    this.rewindVeil.clear().rect(0, 0, W, H).fill(0x9fdfff);
    this.nightVeil.eventMode = "none";
    this.warmVeil.eventMode = "none";
    this.rewindVeil.eventMode = "none";
    c.addChildAt(this.warmVeil, c.getChildIndex(this.clockRing));
    c.addChildAt(this.nightVeil, c.getChildIndex(this.clockRing));
    c.addChild(this.rewindVeil);
  }

  private buildBackground() {
    // Дальние холмы
    const far = new Graphics()
      .moveTo(0, 470)
      .quadraticCurveTo(240, 380, 520, 440)
      .quadraticCurveTo(760, 500, 1024, 420)
      .lineTo(1024, 520).lineTo(0, 520).fill(P.hillFar);
    const mid = new Graphics()
      .moveTo(0, 520)
      .quadraticCurveTo(300, 430, 620, 500)
      .quadraticCurveTo(860, 548, 1024, 486)
      .lineTo(1024, 600).lineTo(0, 600).fill(P.hillMid);
    this.bg.addChild(far, mid);

    // Мост-арка вдалеке
    const bridge = new Container();
    bridge.position.set(430, 452);
    const deck = new Graphics().rect(-70, -8, 140, 12).fill(P.stoneLight).stroke({ color: P.stoneDark, width: 2, alpha: 0.4 });
    const a1 = new Graphics().arc(-36, 4, 22, Math.PI, 0).stroke({ color: P.stoneDark, width: 8, alpha: 0.5 });
    const a2 = new Graphics().arc(36, 4, 22, Math.PI, 0).stroke({ color: P.stoneDark, width: 8, alpha: 0.5 });
    bridge.addChild(a1, a2, deck);
    bridge.scale.set(0.9);
    bridge.alpha = 0.85;
    this.bg.addChild(bridge);

    // Мельница
    const wm = art.makeWindmill();
    wm.position.set(360, 470);
    wm.scale.set(1.15);
    this.windmillBlades = wm.children[wm.children.length - 1] as Container;
    this.bg.addChild(wm);

    // Домики на холме
    for (const [x, y, r, s] of [
      [590, 470, P.dusk, 0.9],
      [660, 486, 0xb24a3a, 1.05],
      [724, 500, P.dusk, 0.85],
    ] as [number, number, number, number][]) {
      const ct = art.makeCottage(r);
      ct.position.set(x, y);
      ct.scale.set(s);
      this.cottageWins.push((ct as any).win as Graphics);
      this.bg.addChild(ct);
    }

    // Ряд дальних деревьев
    const trees = new Graphics();
    for (let x = 60; x < 1024; x += 90) {
      const y = 470 + Math.sin(x) * 12;
      trees.circle(x, y, 26 + (x % 40) * 0.4).fill(P.hillFar);
      trees.rect(x - 3, y, 6, 26).fill(P.bark);
    }
    trees.alpha = 0.75;
    this.bg.addChildAt(trees, 2);
  }

  private buildGround() {
    const base = new Graphics()
      .moveTo(0, 560)
      .quadraticCurveTo(300, 500, 560, 556)
      .quadraticCurveTo(820, 606, 1024, 548)
      .lineTo(1024, 768).lineTo(0, 768).fill(P.meadow);
    // Солнечная возвышенность в среднем плане
    const sun = new Graphics()
      .moveTo(0, 582)
      .quadraticCurveTo(340, 524, 640, 588)
      .quadraticCurveTo(860, 624, 1024, 584)
      .lineTo(1024, 664).lineTo(0, 672).fill(P.meadowSun);
    const sun2 = new Graphics()
      .moveTo(0, 600)
      .quadraticCurveTo(360, 548, 660, 606)
      .lineTo(1024, 596).lineTo(1024, 636).lineTo(0, 644).fill({ color: 0xffffff, alpha: 0.14 });
    // Тень переднего плана
    const shade = new Graphics()
      .moveTo(0, 704)
      .quadraticCurveTo(500, 678, 1024, 708)
      .lineTo(1024, 768).lineTo(0, 768).fill({ color: P.meadowShade, alpha: 0.75 });
    this.ground.addChild(base, sun, sun2, shade);

    // Камни-ступени
    const path = new Graphics();
    for (let i = 0; i <= HORIZON; i++) {
      path.ellipse(pebbleX(i), pebbleY(i) + 6, 26, 12).fill({ color: P.stoneDark, alpha: 0.25 });
      path.ellipse(pebbleX(i), pebbleY(i), 24, 13).fill(mix(P.stoneLight, P.dirt, 0.4)).stroke({ color: P.stoneDark, width: 2, alpha: 0.3 });
      path.ellipse(pebbleX(i) - 5, pebbleY(i) - 4, 9, 5).fill({ color: 0xffffff, alpha: 0.25 });
    }
    this.ground.addChild(path);
  }

  private buildPond() {
    const shadow = new Graphics().ellipse(POND.x, POND.y + 20, 180, 60).fill({ color: P.meadowShade, alpha: 0.35 });
    const water = new Graphics()
      .moveTo(POND.x - 168, POND.y)
      .bezierCurveTo(POND.x - 150, POND.y - 78, POND.x + 120, POND.y - 66, POND.x + 172, POND.y - 6)
      .bezierCurveTo(POND.x + 150, POND.y + 66, POND.x - 120, POND.y + 74, POND.x - 168, POND.y)
      .fill(P.water);
    const deep = new Graphics().ellipse(POND.x + 6, POND.y + 6, 130, 52).fill({ color: P.waterDeep, alpha: 0.5 });
    const shine = new Graphics().ellipse(POND.x - 60, POND.y - 24, 60, 20).fill({ color: P.waterLight, alpha: 0.55 });
    this.ripples.eventMode = "none";
    this.pondC.addChild(shadow, water, deep, shine, this.ripples);

    // Дощатый мостик у левого края пруда
    const dock = new Graphics()
      .poly([POND.x - 200, POND.y - 6, POND.x - 120, POND.y - 14, POND.x - 118, POND.y + 6, POND.x - 200, POND.y + 16])
      .fill(P.wood).stroke({ color: P.woodDark, width: 2, alpha: 0.5 });
    for (let i = 0; i < 4; i++) dock.moveTo(POND.x - 190 + i * 18, POND.y - 10).lineTo(POND.x - 186 + i * 18, POND.y + 12).stroke({ color: P.woodDark, width: 1.5, alpha: 0.4 });
    this.pondC.addChild(dock);

    // Кувшинки
    const pad1 = art.makeLilyPad(34);
    pad1.position.set(LILY_LEAF.x, LILY_LEAF.y);
    const pad2 = art.makeLilyPad(22);
    pad2.position.set(LILY_LEAF.x + 78, LILY_LEAF.y + 20);
    this.pondC.addChild(pad1, pad2);

    this.reeds.position.set(POND.x - 150, POND.y - 40);
    this.pondC.addChild(this.reeds);
  }

  private buildDecor() {
    // Большой камень слева (для улитки)
    const rock = art.makeRock(70, 52);
    rock.position.set(120, 600);
    const rock2 = art.makeRock(34, 26);
    rock2.position.set(196, 636);
    this.decor.addChild(rock, rock2);

    // Куст-дерево справа за курицей
    const tree = art.makeTree();
    tree.position.set(958, 612);
    tree.scale.set(1.25);
    this.decor.addChild(tree);
    const bush = new Graphics().circle(902, 560, 46).circle(946, 574, 40).fill(P.leafDark);
    this.decor.addChild(bush);

    // Гнездо на земле
    const nest = new Graphics()
      .ellipse(NEST.x, NEST.y + 8, 46, 20).fill(P.bark)
      .ellipse(NEST.x, NEST.y + 2, 40, 15).fill(P.woodGrain)
      .ellipse(NEST.x, NEST.y - 2, 30, 9).fill({ color: P.night, alpha: 0.22 });
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      nest.moveTo(NEST.x + Math.cos(a) * 38, NEST.y + 6 + Math.sin(a) * 14)
        .lineTo(NEST.x + Math.cos(a) * 50, NEST.y + 2 + Math.sin(a) * 16)
        .stroke({ color: P.bark, width: 3 });
    }
    // Мшистый пятачок для яйца
    const moss = new Graphics().ellipse(MOSS.x, MOSS.y + 6, 34, 16).fill(P.meadowShade).ellipse(MOSS.x, MOSS.y + 2, 26, 11).fill(P.leaf);
    this.decor.addChild(moss, nest);

    // Забор в середине луга
    const fence = new Graphics();
    for (let i = 0; i < 5; i++) {
      const x = 548 + i * 42;
      fence.roundRect(x - 5, 452, 10, 60, 3).fill(P.woodLight).stroke({ color: P.woodDark, width: 2, alpha: 0.4 });
    }
    fence.roundRect(540, 466, 220, 9, 3).fill(P.wood);
    fence.roundRect(540, 490, 220, 9, 3).fill(P.wood);
    this.decor.addChild(fence);

    // Цветы и трава (детерминированный разброс)
    let seed = 7;
    const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
    for (let i = 0; i < 46; i++) {
      const x = 40 + rnd() * 944;
      const y = 560 + rnd() * 190;
      if (x > 90 && x < 400 && y > 560 && y < 660) continue; // не в пруду
      if (rnd() < 0.5) {
        const fl = art.makeFlower((Math.floor(rnd() * 3) as 0 | 1 | 2));
        fl.position.set(x, y);
        fl.scale.set(0.7 + rnd() * 0.6);
        this.grassTufts.push(fl);
        this.decor.addChild(fl);
      } else {
        const tuft = new Container();
        tuft.addChild(art.makeGrassTuft());
        tuft.position.set(x, y);
        tuft.scale.set(0.7 + rnd() * 0.7);
        this.grassTufts.push(tuft);
        this.decor.addChild(tuft);
      }
    }

    // Свисающая листва в верхних углах (обрамление)
  }

  // Свисающая листва в верхних углах — поверх деревянной арки (как в референсе).
  private buildFrameLeaves(): Container {
    const c = new Container();
    const cluster = (cx: number, cy: number, flip: number) => {
      const g = new Container();
      g.addChild(new Graphics().circle(0, 40, 54).circle(60 * flip, 10, 42).circle(20 * flip, 90, 46).circle(90 * flip, 70, 34).fill(P.leafDark));
      g.addChild(new Graphics().circle(10 * flip, 20, 46).circle(66 * flip, 46, 34).circle(-6 * flip, 70, 30).fill(P.leaf));
      g.addChild(new Graphics().circle(24 * flip, 8, 26).circle(70 * flip, 30, 20).fill(P.leafLight));
      // пара листиков-язычков
      for (let i = 0; i < 3; i++) {
        const lx = (30 + i * 26) * flip;
        g.addChild(
          new Graphics()
            .moveTo(lx, 96).quadraticCurveTo(lx + 16 * flip, 120, lx + 4 * flip, 150).quadraticCurveTo(lx - 10 * flip, 120, lx, 96)
            .fill(i % 2 ? P.leaf : P.leafDark),
        );
      }
      g.position.set(cx, cy);
      return g;
    };
    c.addChild(cluster(-30, -40, 1), cluster(1054, -40, -1));
    return c;
  }

  private buildClock() {
    const ring = this.clockRing;

    // Резной деревянный обод: пологая арка по верху + две боковые стойки.
    // Внешний и внутренний контур арки — квадратичные кривые.
    // Полукруглая деревянная арка. Внешняя кромка — почти у края экрана,
    // внутренняя — пологий купол. Слои сверху-вниз: свет → тон → тень (верхний свет).
    const band = (offY: number, col: number) =>
      new Graphics()
        .moveTo(-40, 300)
        .lineTo(-40, 96 + offY)
        .quadraticCurveTo(512, -150 + offY, 1064, 96 + offY)
        .lineTo(1064, 300)
        .quadraticCurveTo(512, 214 + offY, -40, 300)
        .fill(col);
    ring.addChild(band(0, P.woodLight), band(12, P.wood), band(24, P.woodDark));
    // блик-скос по верхней кромке
    ring.addChild(
      new Graphics()
        .moveTo(-40, 96).quadraticCurveTo(512, -150, 1064, 96)
        .quadraticCurveTo(512, -128, -40, 118)
        .fill({ color: 0xffffff, alpha: 0.18 }),
    );

    // Боковые стойки
    for (const left of [true, false]) {
      const x = left ? -36 : 992;
      ring.addChild(new Graphics().roundRect(x, 150, 68, 620, 16).fill(P.wood).stroke({ color: P.woodDark, width: 3, alpha: 0.4 }));
      ring.addChild(new Graphics().roundRect(x + (left ? 44 : 6), 150, 12, 620, 6).fill({ color: P.woodLight, alpha: 0.55 }));
    }

    // Волокна дерева вдоль арки
    const grain = new Graphics();
    for (let k = 0; k <= 22; k++) {
      const gx = -30 + (k / 22) * 1084;
      const gy = 150 - Math.sin((k / 22) * Math.PI) * 210;
      grain.moveTo(gx, gy - 30).quadraticCurveTo(gx + 6, gy, gx, gy + 40).stroke({ color: P.woodGrain, width: 2, alpha: 0.16 });
    }
    ring.addChild(grain);
    for (let k = 0; k <= 5; k++) {
      const gx = 70 + (k / 5) * 884;
      const gy = 130 - Math.sin((k / 5) * Math.PI) * 200 + 46;
      ring.addChild(
        new Graphics().circle(gx, gy, 6).fill(P.woodDark).circle(gx - 1.5, gy - 1.5, 2.5).fill({ color: P.woodLight, alpha: 0.7 }),
      );
    }

    // Цифры 0..10 — вырезаны в дереве (тёмная с верхним бликом)
    for (let i = 0; i <= HORIZON; i++) {
      const x = numX(i);
      const y = numY(i);
      const hi = new Text({ text: String(i), style: { fontSize: 30, fontFamily: "Georgia, serif", fontWeight: "700", fill: P.woodLight } });
      hi.anchor.set(0.5);
      hi.position.set(x - 1.5, y - 2);
      const lo = new Text({ text: String(i), style: { fontSize: 30, fontFamily: "Georgia, serif", fontWeight: "700", fill: P.woodGrain } });
      lo.anchor.set(0.5);
      lo.position.set(x, y);
      ring.addChild(hi, lo);
      const dots = new Graphics();
      const n = Math.max(1, i);
      for (let d = 0; d < n; d++) dots.circle(-((n - 1) * 3) + d * 6, 0, 1.7).fill({ color: P.woodGrain, alpha: 0.55 });
      dots.position.set(x, y + 15);
      ring.addChild(dots);
    }

    // Стрелка-маятник от нижней ступицы к текущей цифре
    this.hand.position.set(HUB.x, HUB.y);
    const hub = new Graphics()
      .circle(HUB.x, HUB.y, 26).fill(P.woodDark)
      .circle(HUB.x, HUB.y, 18).fill(P.woodGrain)
      .circle(HUB.x - 5, HUB.y - 5, 6).fill({ color: P.woodLight, alpha: 0.7 });
    ring.addChild(this.hand, hub);

    // Крупная текущая цифра по центру верха — на утопленной табличке
    const plaqueY = 66;
    ring.addChild(
      new Graphics()
        .ellipse(512, plaqueY, 62, 52).fill(P.woodDark)
        .ellipse(512, plaqueY + 2, 54, 44).fill(P.woodGrain)
        .ellipse(512, plaqueY - 4, 40, 24).fill({ color: 0x000000, alpha: 0.12 }),
    );
    this.bigDigitShadow.anchor.set(0.5);
    this.bigDigit.anchor.set(0.5);
    this.bigDigitShadow.position.set(512 + 3, plaqueY + 4);
    this.bigDigit.position.set(512, plaqueY);
    ring.addChild(this.bigDigitShadow, this.bigDigit);
  }

  private buildBubble() {
    const g = new Graphics()
      .roundRect(-120, -54, 240, 88, 22).fill(P.bubble).stroke({ color: P.bubbleInk, width: 2, alpha: 0.25 });
    g.poly([-30, 30, -6, 30, -34, 58]).fill(P.bubble);
    this.bubbleText.anchor.set(0.5);
    this.bubble.addChild(g, this.bubbleText);
    this.bubble.position.set(CUCKOO_HOME.x + 150, CUCKOO_HOME.y - 6);
    this.bubble.visible = false;
  }

  private buildThought() {
    const g = new Graphics()
      .circle(0, 0, 24).fill({ color: 0xffffff, alpha: 0.92 })
      .circle(-20, 20, 7).fill({ color: 0xffffff, alpha: 0.9 })
      .circle(-30, 32, 4).fill({ color: 0xffffff, alpha: 0.85 });
    const mini = art.makeChick();
    mini.node.scale.set(0.5);
    mini.node.position.set(0, 2);
    this.thought.addChild(g, mini.node);
    this.thought.visible = false;
  }

  // -------------------------------------------------------------- drag
  private sprites(): Record<DraggableId, Container> {
    return { egg: this.egg.node, fly: this.fly.node, salad: this.salad.node };
  }

  private bindDrag() {
    for (const [id, node] of Object.entries(this.sprites()) as [DraggableId, Container][]) {
      node.eventMode = "static";
      node.cursor = "pointer";
      node.on("pointerdown", (e: FederatedPointerEvent) => this.onPointerDown(id, e));
    }
    this.container.eventMode = "static";
    this.container.hitArea = { contains: () => true } as any;
    this.container.on("globalpointermove", (e) => this.onPointerMove(e));
    this.container.on("pointerup", (e) => this.onPointerUp(e));
    this.container.on("pointerupoutside", (e) => this.onPointerUp(e));
  }

  get isDragging(): boolean {
    return this.draggingId !== null;
  }

  private onPointerDown(id: DraggableId, e: FederatedPointerEvent) {
    if (this.frozenMoment === null) return;
    const st = stateAt(this.edits.list(), this.frozenMoment);
    if (id === "egg" && st.egg.hatched) return;
    if (id === "fly" && st.fly.eaten) return;
    this.draggingId = id;
    const node = this.sprites()[id];
    const p = e.getLocalPosition(this.container);
    this.dragOffset = { x: node.x - p.x, y: node.y - p.y };
    node.scale.set(1.3);
    this.cb.onDragStart(id);
  }

  private onPointerMove(e: FederatedPointerEvent) {
    if (!this.draggingId) return;
    const node = this.sprites()[this.draggingId];
    const p = e.getLocalPosition(this.container);
    node.position.set(p.x + this.dragOffset.x, p.y + this.dragOffset.y);
  }

  private onPointerUp(e: FederatedPointerEvent) {
    const id = this.draggingId;
    if (id === null || this.frozenMoment === null) return;
    this.draggingId = null;
    this.sprites()[id].scale.set(1);
    const p = e.getLocalPosition(this.container);
    const tick = this.frozenMoment;

    if (id === "egg") {
      const inNest = Math.hypot(p.x - NEST.x, p.y - NEST.y) < 64;
      const onMoss = Math.hypot(p.x - MOSS.x, p.y - MOSS.y) < 60;
      if (inNest) this.cb.onEdit("egg", tick, { kind: "egg", place: "nest" });
      else if (onMoss) this.cb.onEdit("egg", tick, { kind: "egg", place: "moss" });
    } else if (id === "fly") {
      const onLeaf = Math.hypot(p.x - LILY_LEAF.x, p.y - LILY_LEAF.y) < 58;
      this.cb.onEdit("fly", tick, {
        kind: "fly",
        value: { onLeaf, x: onLeaf ? LILY_LEAF.x : p.x, y: onLeaf ? LILY_LEAF.y : p.y },
      });
    } else if (id === "salad") {
      let best = 0;
      let bestD = Infinity;
      for (let i = 0; i <= HORIZON; i++) {
        const dd = Math.abs(p.x - pebbleX(i)) + Math.abs(p.y - pebbleY(i));
        if (dd < bestD) {
          bestD = dd;
          best = i;
        }
      }
      this.cb.onEdit("salad", tick, { kind: "salad", pebble: best });
    }
  }

  // --------------------------------------------------------- public API
  setFrozen(moment: number | null) {
    this.frozenMoment = moment;
  }

  setRewinding(on: boolean) {
    this.rewinding = on;
  }

  showCuckoo(out: boolean) {
    this.cuckooOut = out;
  }

  cuckooSay(text: string) {
    if (!text) return;
    this.bubbleText.text = text;
    this.bubble.visible = true;
    this.bubble.scale.set(0.6);
    this.bubbleUntil = performance.now() + 2600 + text.length * 45;
  }

  // -------------------------------------------------------------- frame
  renderAt(t: number, nowMs: number) {
    const edits = this.edits.list();
    const lo = Math.max(0, Math.floor(t));
    const hi = Math.min(HORIZON, Math.ceil(t));
    const f = t - lo;
    const a = stateAt(edits, lo);
    const b = stateAt(edits, hi);
    const frozen = this.frozenMoment !== null;
    const breathe = 1 + Math.sin(nowMs / 900) * 0.02;

    this.renderSky(t, nowMs);
    this.renderClock(t, nowMs);
    this.renderCuckoo(t, nowMs);
    this.renderSnailSalad(a, b, f, t, nowMs);
    this.renderFrog(a, b, f, nowMs, breathe);
    this.renderFly(a, b, f, nowMs);
    this.renderHen(a, b, f, nowMs, breathe);
    this.renderEggChick(a, b, f, hi, nowMs);
    this.renderDecor(nowMs);
    this.renderInteractiveHint(nowMs, frozen);
    this.renderVeils(t);
    this.renderSparkles(nowMs);

    // Облачко желания курицы
    this.thought.visible = b.hen.wantsChick && frozen;
    if (this.thought.visible) {
      this.thought.position.set(this.hen.node.x + 34, this.hen.node.y - 78 + Math.sin(nowMs / 700) * 3);
    }

    // Речевое облачко
    if (this.bubble.visible) {
      this.bubble.scale.set(lerp(this.bubble.scale.x, 1, 0.2));
      if (performance.now() > this.bubbleUntil) this.bubble.visible = false;
    }
  }

  private renderSky(t: number, nowMs: number) {
    const top = mix(P.skyTop, P.skyMid, ramp([{ t: 0, v: 0 }, { t: 5, v: 0.55 }, { t: 10, v: 0 }], t));
    // Мягкий градиент неба: несколько полос top → haze.
    this.skyBase.clear();
    const bands = 7;
    for (let i = 0; i < bands; i++) {
      const y = (i / bands) * 560;
      this.skyBase.rect(0, y, W, 560 / bands + 1).fill(mix(top, P.skyHaze, i / (bands - 1)));
    }
    // мягкое свечение у горизонта / вокруг солнца
    const sunX = lerp(120, 904, t / HORIZON);
    const sunY = 470 - Math.sin((Math.PI * t) / HORIZON) * 372;
    this.horizonGlow.clear();
    for (let r = 240; r > 0; r -= 40) this.horizonGlow.circle(sunX, sunY, r).fill({ color: P.sun, alpha: 0.06 });
    this.horizonGlow.rect(0, 430, W, 90).fill({ color: P.sunGold, alpha: 0.12 });

    const sunUp = clamp01((9.7 - t) / 0.5) * clamp01((t - 0.4) / 0.5);
    this.sun.clear();
    if (sunUp > 0.01) {
      this.sun.circle(sunX, sunY, 46).fill({ color: P.sunCore, alpha: sunUp }).circle(sunX, sunY, 34).fill({ color: 0xffffff, alpha: sunUp * 0.8 });
    }
    const nightN = clamp01((t - 8.6) / 1.2) + clamp01((1.4 - t) / 1.2);
    this.moon.clear();
    if (nightN > 0.02) {
      const mx = lerp(904, 120, t / HORIZON);
      const my = 150 + Math.sin((Math.PI * t) / HORIZON) * 40;
      this.moon.circle(mx, my, 30).fill({ color: P.moon, alpha: Math.min(1, nightN) }).circle(mx + 10, my - 6, 24).fill({ color: top, alpha: Math.min(1, nightN) });
    }
    if (this.stars.children.length === 0) {
      let s = 3;
      const r = () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
      for (let i = 0; i < 46; i++) {
        this.stars.addChild(new Graphics().circle(r() * W, r() * 300, r() * 1.6 + 0.5).fill(P.star));
      }
    }
    this.stars.alpha = Math.min(1, nightN) * (0.6 + Math.sin(nowMs / 600) * 0.15);

    // дрейф облаков (в видимой полосе неба между аркой и холмами)
    if (this.clouds.children.length === 0) {
      for (const [x, y, sc] of [[160, 300, 1.5], [470, 250, 1.9], [800, 330, 1.25], [620, 380, 1], [980, 270, 1.3]] as [number, number, number][]) {
        const cl = art.makeCloud(sc);
        cl.position.set(x, y);
        this.clouds.addChild(cl);
      }
    }
    const drift = this.rewinding ? -1 : 1;
    this.clouds.children.forEach((cl, i) => {
      cl.x += drift * (0.1 + i * 0.03);
      if (cl.x > W + 160) cl.x = -160;
      if (cl.x < -160) cl.x = W + 160;
    });
    this.clouds.alpha = clamp01((t - 0.5) / 1) * clamp01((9.5 - t) / 1) * 0.9 + 0.1;
  }

  private renderClock(t: number, nowMs: number) {
    // Стрелка часов: конус от ступицы к текущей цифре, с бликом и хвостом-противовесом.
    const tipX = numX(t) - HUB.x;
    const tipY = numY(t) - HUB.y - 4;
    const len = Math.hypot(tipX, tipY) || 1;
    const ux = tipX / len;
    const uy = tipY / len;
    const px = -uy;
    const py = ux;
    const baseW = 9;
    this.hand.clear();
    this.hand
      .poly([
        px * baseW, py * baseW,
        tipX - ux * 12 + px * 2, tipY - uy * 12 + py * 2,
        tipX, tipY,
        tipX - ux * 12 - px * 2, tipY - uy * 12 - py * 2,
        -px * baseW, -py * baseW,
        -ux * 26, -uy * 26,
      ])
      .fill(P.woodDark);
    this.hand
      .poly([px * 3, py * 3, tipX - ux * 16 + px * 1, tipY - uy * 16 + py * 1, tipX - ux * 16 - px * 1, tipY - uy * 16 - py * 1, -px * 3, -py * 3])
      .fill({ color: P.woodLight, alpha: 0.55 });
    this.hand.circle(tipX, tipY, 6).fill(P.dusk).circle(tipX - 2, tipY - 2, 2).fill({ color: 0xffffff, alpha: 0.7 });

    const d = Math.round(t);
    if (d !== this.digitShown) {
      this.digitShown = d;
      this.digitPop = 1;
      this.bigDigit.text = String(d);
      this.bigDigitShadow.text = String(d);
    }
    this.digitPop = Math.max(0, this.digitPop - 0.06);
    const scl = 1 + this.digitPop * 0.28 + Math.sin(nowMs / 1400) * 0.015;
    this.bigDigit.scale.set(scl);
    this.bigDigitShadow.scale.set(scl);
  }

  private renderCuckoo(t: number, nowMs: number) {
    const out = this.cuckooOut || t > HORIZON - 0.35;
    const targetY = CUCKOO_HOME.y + (out ? 30 : 6);
    const n = this.cuckoo.node;
    n.y = lerp(n.y, targetY, 0.18);
    n.rotation = Math.sin(nowMs / 1100) * 0.04;
    (this.cuckoo.parts.beakOpenGrp as Container).visible = out && Math.sin(nowMs / 90) > 0;
    (this.cuckoo.parts.beakClosed as Graphics).visible = !((this.cuckoo.parts.beakOpenGrp as Container).visible);
    const blink = (nowMs % 3600) < 130;
    (this.cuckoo.parts.blink as Graphics).visible = blink;
  }

  private renderSnailSalad(
    a: ReturnType<typeof stateAt>,
    b: ReturnType<typeof stateAt>,
    f: number,
    t: number,
    nowMs: number,
  ) {
    const sx = lerp(pebbleX(a.snail.pebble), pebbleX(b.snail.pebble), f);
    const sy = lerp(pebbleY(a.snail.pebble), pebbleY(b.snail.pebble), f) - 16;
    this.snail.node.position.set(sx, sy);
    this.snail.node.scale.set(BASE.snail, BASE.snail + Math.sin(nowMs / 700) * 0.03);
    this.shSnail.position.set(sx, sy + 26);
    this.shSnail.scale.set(1.3, 1);
    const sway = Math.sin(nowMs / 500) * 0.12;
    (this.snail.parts.hornL as Graphics).rotation = sway;
    (this.snail.parts.hornR as Graphics).rotation = sway * 0.7;
    (this.snail.parts.hornTipL as Graphics).rotation = sway;
    (this.snail.parts.hornTipR as Graphics).rotation = sway;

    if (this.draggingId !== "salad") {
      this.salad.node.position.set(pebbleX(b.salad.pebble), pebbleY(b.salad.pebble) - 18);
    }
    this.salad.node.alpha = b.snail.eating && f > 0.5 ? 0.4 : 1;
    this.shSalad.position.set(this.salad.node.x, pebbleY(b.salad.pebble) + 8);
    this.shSalad.visible = this.draggingId !== "salad";
    void t;
  }

  private renderFrog(
    a: ReturnType<typeof stateAt>,
    b: ReturnType<typeof stateAt>,
    f: number,
    nowMs: number,
    breathe: number,
  ) {
    const from = a.frog.place === "leaf" ? FROG_LEAF : FROG_POND;
    const to = b.frog.place === "leaf" ? FROG_LEAF : FROG_POND;
    const jumping = from !== to;
    const x = lerp(from.x, to.x, f);
    const groundY = lerp(from.y, to.y, f);
    const y = groundY - (jumping ? Math.sin(Math.PI * f) * 104 : 0);
    this.frog.node.position.set(x, y);
    const sq = jumping ? 1 + Math.sin(Math.PI * f) * 0.16 : breathe;
    this.frog.node.scale.set((jumping ? 2 - sq : 1) * BASE.frog, sq * BASE.frog);
    this.shFrog.position.set(x, groundY + 16);
    this.shFrog.scale.set(1.1 - (jumping ? Math.sin(Math.PI * f) * 0.5 : 0), 1);
    this.shFrog.alpha = jumping ? 0.7 : 1;
    const happy = b.frog.hasFly && f > 0.4;
    (this.frog.parts.smileWide as Graphics).visible = happy;
    (this.frog.parts.smile as Graphics).visible = !happy;
    void nowMs;
  }

  private renderFly(
    a: ReturnType<typeof stateAt>,
    b: ReturnType<typeof stateAt>,
    f: number,
    nowMs: number,
  ) {
    if (this.draggingId === "fly") {
      (this.fly.parts.wingL as Graphics).rotation = Math.sin(nowMs / 40) * 0.5;
      (this.fly.parts.wingR as Graphics).rotation = -Math.sin(nowMs / 40) * 0.5;
      return;
    }
    if (b.fly.eaten) {
      this.fly.node.visible = false;
      return;
    }
    this.fly.node.visible = true;
    if (b.fly.place === "reeds") {
      this.fly.node.position.set(520 + Math.sin(nowMs / 320) * 46, 330 + Math.cos(nowMs / 210) * 26);
    } else {
      this.fly.node.position.set(lerp(a.fly.x, b.fly.x, f), lerp(a.fly.y, b.fly.y, f) + Math.sin(nowMs / 180) * 2);
    }
    (this.fly.parts.wingL as Graphics).rotation = Math.sin(nowMs / 40) * 0.5;
    (this.fly.parts.wingR as Graphics).rotation = -Math.sin(nowMs / 40) * 0.5;
  }

  private renderHen(
    a: ReturnType<typeof stateAt>,
    b: ReturnType<typeof stateAt>,
    f: number,
    nowMs: number,
    breathe: number,
  ) {
    const from = a.hen.place === "nest" ? HEN_NEST : HEN_FENCE;
    const to = b.hen.place === "nest" ? HEN_NEST : HEN_FENCE;
    const walking = from !== to;
    const x = lerp(from.x, to.x, f);
    const y = lerp(from.y, to.y, f) - (walking ? Math.abs(Math.sin(f * Math.PI * 4)) * 8 : 0);
    this.hen.node.position.set(x, y);
    this.hen.node.scale.set(BASE.hen * 0.99, BASE.hen * breathe);
    this.shHen.position.set(x, lerp(from.y, to.y, f) + 30);
    this.shHen.scale.set(1.8, 1);
    (this.hen.parts.wing as Graphics).rotation = walking ? Math.sin(nowMs / 80) * 0.25 : Math.sin(nowMs / 900) * 0.05;
  }

  private renderEggChick(
    a: ReturnType<typeof stateAt>,
    b: ReturnType<typeof stateAt>,
    f: number,
    hi: number,
    nowMs: number,
  ) {
    // Яйцо
    if (this.draggingId !== "egg") {
      const from = a.egg.place === "nest" ? EGG_NEST : MOSS;
      const to = b.egg.place === "nest" ? EGG_NEST : MOSS;
      this.egg.node.position.set(lerp(from.x, to.x, f), lerp(from.y, to.y, f));
    }
    const hatchingNow = b.egg.hatched && b.egg.hatchedAt === hi && f < 0.55;
    this.egg.node.visible = !b.egg.hatched || hatchingNow;
    (this.egg.parts.glow as Graphics).clear().circle(0, -4, 26 + Math.sin(nowMs / 260) * 3)
      .fill({ color: P.sunCore, alpha: !b.egg.hatched && b.egg.place === "nest" ? 0.12 + b.egg.warmth * 0.06 : 0.0 });
    const crack = this.egg.parts.crack as Graphics;
    crack.clear();
    if (!b.egg.hatched && b.egg.warmth >= 3) {
      crack.moveTo(-8, -6).lineTo(-2, 0).lineTo(-6, 6).lineTo(2, 10).stroke({ color: P.eggShade, width: 2 });
      if (b.egg.warmth >= 4) crack.moveTo(4, -10).lineTo(8, -2).lineTo(3, 4).stroke({ color: P.eggShade, width: 2 });
      crack.visible = true;
    } else if (hatchingNow) {
      crack.moveTo(-14, -2).lineTo(-4, 6).lineTo(-10, 12).moveTo(2, -6).lineTo(12, 2).stroke({ color: P.eggShade, width: 3 });
      crack.visible = true;
    } else {
      crack.visible = false;
    }

    // Точки тепла над гнездом
    this.warmthDots.removeChildren();
    if (!b.egg.hatched && b.egg.place === "nest") {
      for (let i = 0; i < 5; i++) {
        this.warmthDots.addChild(
          new Graphics()
            .circle(this.egg.node.x - 16 + i * 8, this.egg.node.y - 32, 3)
            .fill({ color: i < b.egg.warmth ? P.sunCore : 0xffffff, alpha: i < b.egg.warmth ? 1 : 0.35 }),
        );
      }
    }

    // Цыплёнок
    const show = b.chick.exists && !hatchingNow;
    this.chick.node.visible = show;
    if (show) {
      const base = b.chick.underWing ? { x: HEN_NEST.x + 4, y: HEN_NEST.y + 6 } : { x: NEST.x - 8, y: NEST.y - 10 };
      const hop = b.chick.asleep ? 0 : Math.abs(Math.sin(nowMs / 260)) * (b.chick.crying ? 2 : 5);
      const wob = b.chick.crying ? Math.sin(nowMs / 70) * 2 : 0;
      this.chick.node.position.set(base.x + wob, base.y - hop);
      this.chick.node.scale.set(BASE.chick * (b.chick.underWing ? 0.82 : 1));
      this.shChick.position.set(base.x + wob, base.y + 20);
      this.shChick.scale.set(0.6 - hop * 0.03, 0.5);
      (this.chick.parts.tear as Graphics).visible = b.chick.crying && Math.sin(nowMs / 400) > 0;
      (this.chick.parts.zzz as Text).visible = b.chick.asleep;
      (this.chick.parts.shut as Graphics).visible = b.chick.asleep;
    }
    this.shChick.visible = show;
  }

  private renderDecor(nowMs: number) {
    if (this.windmillBlades) this.windmillBlades.rotation += this.rewinding ? -0.006 : 0.006;
    this.reeds.rotation = Math.sin(nowMs / 800) * 0.05;
    const sway = Math.sin(nowMs / 700) * 0.05 * (this.rewinding ? -1 : 1);
    for (const g of this.grassTufts) g.rotation = sway * (0.6 + (g.x % 7) / 10);

    // Рябь на воде
    this.ripples.clear();
    const dir = this.rewinding ? -1 : 1;
    for (let k = 0; k < 3; k++) {
      const phase = ((nowMs / 1600 + k / 3) % 1);
      const rr = dir > 0 ? phase : 1 - phase;
      this.ripples.ellipse(POND.x + 20, POND.y, 30 + rr * 120, 12 + rr * 44)
        .stroke({ color: P.waterLight, width: 2, alpha: 0.35 * (1 - rr) });
    }

    // Свет в окнах домиков ночью
    const nightAlpha = this.nightVeil.alpha;
    for (const w of this.cottageWins) w.tint = nightAlpha > 0.35 ? 0xffd97a : 0xffffff;
  }

  private renderInteractiveHint(nowMs: number, frozen: boolean) {
    const pulse = frozen ? 1 + Math.sin(nowMs / 300) * 0.07 : 1 + Math.sin(nowMs / 900) * 0.02;
    for (const [id, node] of Object.entries(this.sprites()) as [DraggableId, Container][]) {
      if (this.draggingId === id) continue;
      node.scale.set(pulse * (BASE[id] ?? 1));
    }
    // мягкое свечение под перетаскиваемыми предметами в заморозке
    (this.egg.parts.glow as Graphics).alpha = frozen ? 0.6 + Math.sin(nowMs / 320) * 0.3 : (this.egg.parts.glow as Graphics).alpha;
  }

  private renderVeils(t: number) {
    this.nightVeil.alpha = ramp(
      [{ t: 0, v: 0.5 }, { t: 1.4, v: 0.1 }, { t: 2.6, v: 0 }, { t: 8.2, v: 0 }, { t: 9.3, v: 0.24 }, { t: 10, v: 0.56 }],
      t,
    );
    this.warmVeil.alpha = ramp(
      [{ t: 0, v: 0.04 }, { t: 1.4, v: 0.22 }, { t: 3, v: 0.03 }, { t: 6.5, v: 0.08 }, { t: 8, v: 0.24 }, { t: 9, v: 0.36 }, { t: 10, v: 0.1 }],
      t,
    );
    this.rewindVeil.alpha = lerp(this.rewindVeil.alpha, this.rewinding ? 0.09 : 0, 0.15);
  }

  private renderSparkles(nowMs: number) {
    if (this.rewinding && Math.random() < 0.4) {
      const g = new Graphics().circle(0, 0, 2 + Math.random() * 2).fill({ color: 0xdffcff, alpha: 0.9 });
      g.position.set(Math.random() * W, 200 + Math.random() * 460);
      (g as any)._life = 1;
      this.sparkles.addChild(g);
    }
    for (const g of [...this.sparkles.children] as any[]) {
      g._life -= 0.03;
      g.x -= 3.5;
      g.y -= 0.6;
      g.alpha = Math.max(0, g._life);
      if (g._life <= 0) this.sparkles.removeChild(g);
    }
    void nowMs;
  }
}
